import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import ClientLogos from './components/ClientLogos';
import AgentsGrid from './components/AgentsGrid';
import Stats from './components/Stats';
import UseCases from './components/UseCases';
import BeforeAfter from './components/BeforeAfter';
import Testimonials from './components/Testimonials';
import ClientResults from './components/ClientResults';
import ROICalculator from './components/ROICalculator';
import Certifications from './components/Certifications';
import Pricing from './components/Pricing';
import PromoBanner from './components/PromoBanner';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import { useCachedData } from '../../hooks/useCachedData';
import { agents } from '../../mocks/agents';

export default function Home() {
  // Précharger les données des agents dans le cache
  const { data: cachedAgents } = useCachedData({
    key: 'agents-list',
    fetchFunction: async () => {
      await new Promise(resolve => setTimeout(resolve, 100));
      return agents;
    },
    duration: 15 * 60 * 1000 // Cache de 15 minutes
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags from embed_code
    document.title = 'Agent IA Pro  - Agents IA Pour Entreprises';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'AgentIA Pro Paris propose des agents IA intelligents pour automatiser votre service client, ventes et support technique. Agents IA conversationnels augmentent votre productivité de 300% avec nos solutions d\'intelligence artificielle. Agents IA pour entreprises à Paris. Essai gratuit 14 jours sans engagement. Support client 24/7.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'AgentIA Pro Paris propose des agents IA intelligents pour automatiser votre service client, ventes et support technique. Agents IA conversationnels augmentent votre productivité de 300% avec nos solutions d\'intelligence artificielle. Agents IA pour entreprises à Paris. Essai gratuit 14 jours sans engagement. Support client 24/7.';
      document.head.appendChild(meta);
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'agent IA, intelligence artificielle pour les entreprises, automatisation service client, transformation digitale IA, productivité entreprise automatisation.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'agent IA, intelligence artificielle pour les entreprises, automatisation service client, transformation digitale IA, productivité entreprise automatisation.';
      document.head.appendChild(meta);
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`);
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`);
      document.head.appendChild(canonical);
    }

    // Last Modified
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    } else {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
      document.head.appendChild(lastModified);
    }

    // Open Graph Tags
    const ogTags = [
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/` },
      { property: 'og:title', content: 'Agent IA Pro - Agents IA Pour Entreprises' },
      { property: 'og:description', content: 'AgentIA Pro Paris propose des agents IA intelligents pour automatiser votre service client, ventes et support technique.' },
      { property: 'og:image', content: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/og-image.jpg` }
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    });

    // Twitter Tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Agent IA Pro - Agents IA Pour Entreprises' },
      { name: 'twitter:description', content: 'AgentIA Pro Paris propose des agents IA intelligents pour automatiser votre service client.' },
      { name: 'twitter:image', content: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/og-image.jpg` }
    ];

    twitterTags.forEach(({ name, content }) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    });

    // Schema.org JSON-LD
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(s => s.remove());

    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Agent IA Pro",
      "description": "Plateforme leader d'agents IA intelligents pour entreprises à Paris",
      "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/5645efc0cd1a8c3e708dcbbf89a13d5d/11fa066ec64ff2ace32ca9f90366c703.png"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "37 la grange",
        "addressLocality": "Capian",
        "postalCode": "33550",
        "addressCountry": "FR"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+33-6-76-77-39-01",
        "email": "theo.lalanne0@gmail.com",
        "contactType": "customer service",
        "areaServed": "FR",
        "availableLanguage": "French"
      },
      "sameAs": [
        "https://linkedin.com",
        "https://twitter.com",
        "https://facebook.com"
      ]
    });
    document.head.appendChild(orgScript);

    const websiteScript = document.createElement('script');
    websiteScript.type = 'application/ld+json';
    websiteScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Agent IA Pro",
      "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
      "description": "Plateforme d'agents IA intelligents pour automatiser votre service client, ventes et support technique",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    });
    document.head.appendChild(websiteScript);

    const softwareScript = document.createElement('script');
    softwareScript.type = 'application/ld+json';
    softwareScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Agent IA Pro",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "Agents IA intelligents pour automatiser votre service client, ventes et support technique",
      "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR",
        "description": "Essai gratuit 14 jours sans engagement"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "500",
        "bestRating": "5"
      }
    });
    document.head.appendChild(softwareScript);

    // Schema.org FAQPage
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Qu'est-ce qu'un agent IA et comment peut-il aider mon entreprise ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Un agent IA est un assistant intelligent capable de gérer automatiquement des tâches répétitives comme le service client, la qualification de leads ou le support technique. Il répond 24h/24, 7j/7, réduit vos coûts opérationnels et augmente la satisfaction client jusqu'à 300%."
          }
        },
        {
          "@type": "Question",
          "name": "Combien de temps faut-il pour déployer un agent IA ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "La mise en place d'un agent IA sur Agent IA Pro prend moins de 30 minutes. Notre assistant de configuration guidé vous permet de personnaliser votre agent, d'importer votre base de connaissances et de le connecter à vos outils existants (CRM, WhatsApp, email) sans aucune compétence technique."
          }
        },
        {
          "@type": "Question",
          "name": "Y a-t-il un essai gratuit disponible ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Oui, Agent IA Pro propose un essai gratuit de 14 jours sans engagement et sans carte bancaire requise. Vous avez accès à toutes les fonctionnalités pendant la période d'essai pour tester la plateforme avec vos propres données."
          }
        },
        {
          "@type": "Question",
          "name": "Quelles intégrations sont disponibles avec Agent IA Pro ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Agent IA Pro s'intègre nativement avec WhatsApp, Gmail, Slack, Salesforce, HubSpot, Zendesk, Shopify et plus de 50 autres outils via notre API et nos webhooks. La connexion se fait en quelques clics depuis le tableau de bord."
          }
        },
        {
          "@type": "Question",
          "name": "Mes données sont-elles sécurisées sur Agent IA Pro ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolument. Agent IA Pro est conforme au RGPD, hébergé en Europe et certifié ISO 27001. Toutes les données sont chiffrées en transit et au repos. Vos données ne sont jamais utilisées pour entraîner des modèles tiers."
          }
        },
        {
          "@type": "Question",
          "name": "Puis-je personnaliser le comportement et le ton de mon agent IA ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Oui, chaque agent est entièrement personnalisable : nom, avatar, ton de communication, langue, domaines de compétence et règles de réponse. Vous pouvez également alimenter votre agent avec vos propres documents, FAQ et données métier pour des réponses précises et adaptées à votre secteur."
          }
        },
        {
          "@type": "Question",
          "name": "Quel support est disponible si j'ai besoin d'aide ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Notre équipe support est disponible 24h/24 et 7j/7 par chat, email et téléphone. Les clients sur les plans Pro et Enterprise bénéficient d'un gestionnaire de compte dédié et d'un onboarding personnalisé pour garantir le succès de leur déploiement."
          }
        },
        {
          "@type": "Question",
          "name": "Agent IA Pro fonctionne-t-il pour les PME comme pour les grandes entreprises ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Oui, la plateforme est conçue pour s'adapter à toutes les tailles d'entreprise. Les PME profitent d'une mise en place rapide et d'un ROI immédiat, tandis que les grandes entreprises bénéficient d'une infrastructure scalable, d'un SLA garanti et d'intégrations avancées avec leurs systèmes existants."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    // Schema.org AggregateRating + Reviews
    const reviewScript = document.createElement('script');
    reviewScript.type = 'application/ld+json';
    reviewScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Agent IA Pro",
      "description": "Plateforme d'agents IA intelligents pour automatiser le service client, les ventes et le support technique des entreprises.",
      "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
      "brand": {
        "@type": "Brand",
        "name": "Agent IA Pro"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "bestRating": "5",
        "worstRating": "1",
        "reviewCount": "500",
        "ratingCount": "500"
      },
      "review": [
        {
          "@type": "Review",
          "name": "Taux de conversion multiplié par 3 grâce à l'Agent IA Vendeur",
          "reviewBody": "Depuis que nous avons activé l'Agent IA Vendeur, nos conversions ont augmenté de 340%. C'est comme avoir 10 commerciaux d'élite qui ne dorment jamais. En 3 mois, notre taux de conversion a triplé. L'agent répond instantanément aux questions techniques et guide les clients vers l'achat.",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5",
            "worstRating": "1"
          },
          "datePublished": "2025-04-15",
          "author": {
            "@type": "Person",
            "name": "Sophie Mercier"
          },
          "publisher": {
            "@type": "Organization",
            "name": "TechFlow Solutions"
          }
        },
        {
          "@type": "Review",
          "name": "5000 demandes par jour traitées avec seulement 2 personnes",
          "reviewBody": "Notre service client traite maintenant 5000 demandes par jour avec seulement 2 personnes. L'Agent IA gère 92% des requêtes automatiquement. Un ROI incroyable. Nous ne pouvions pas imaginer une telle efficacité avant de déployer la solution.",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5",
            "worstRating": "1"
          },
          "datePublished": "2025-03-22",
          "author": {
            "@type": "Person",
            "name": "Marc Dubois"
          },
          "publisher": {
            "@type": "Organization",
            "name": "E-Commerce Pro"
          }
        },
        {
          "@type": "Review",
          "name": "Recrutement révolutionné, -80% de temps de pré-qualification",
          "reviewBody": "L'Agent IA RH a révolutionné notre recrutement. Nous avons réduit le temps de pré-qualification de 80% et trouvons des candidats parfaits en quelques heures. Une solution indispensable pour toute équipe RH en croissance.",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5",
            "worstRating": "1"
          },
          "datePublished": "2025-05-10",
          "author": {
            "@type": "Person",
            "name": "Amélie Laurent"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Innovation Labs"
          }
        },
        {
          "@type": "Review",
          "name": "Zéro patient perdu, no-shows divisés par 4",
          "reviewBody": "Nous ne perdons plus un seul patient par manque de disponibilité. L'agent gère les prises de RDV 24/7, envoie des rappels automatiques, et nos no-shows ont chuté de 78%. Notre secrétaire peut enfin se concentrer sur l'accueil et le relationnel. C'est une révolution pour notre clinique.",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5",
            "worstRating": "1"
          },
          "datePublished": "2025-02-18",
          "author": {
            "@type": "Person",
            "name": "Dr. Jean Dupont"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Clinique Santé Plus"
          }
        },
        {
          "@type": "Review",
          "name": "87% des tickets résolus automatiquement, churn -71%",
          "reviewBody": "Notre support était notre point faible, c'est devenu notre force. L'agent résout 87% des demandes instantanément avec des tutoriels interactifs. Notre équipe gère maintenant les cas complexes et améliore le produit. Le churn a chuté de 71%, ce qui représente 95 000€ de revenus conservés chaque mois.",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5",
            "worstRating": "1"
          },
          "datePublished": "2025-06-03",
          "author": {
            "@type": "Person",
            "name": "Julien Moreau"
          },
          "publisher": {
            "@type": "Organization",
            "name": "SaaS Academy"
          }
        }
      ]
    });
    document.head.appendChild(reviewScript);
  }, []);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen bg-black"
    >
      <Navbar />
      <Hero />
      <ClientLogos />
      <AgentsGrid agents={cachedAgents || agents} />
      <UseCases />
      <Stats />
      <BeforeAfter />
      <Testimonials />
      <ClientResults />
      <ROICalculator />
      <Certifications />
      <PromoBanner />
      <Pricing />
      <CTASection />
      <Footer />
    </motion.div>
  );
}
